import { ChangeDetectionStrategy, Component, computed, inject, input, output, resource } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { MappingOfStatusesService, TaxReturnStatusesService, WkTranslatePipe } from "@app/shared";
import { ITaxReturnStatus } from "@app/views-interfaces/tax-return-status-model.interface";
import { JobsBillingCard } from "@app/views-interfaces";
import { JobsAndBillingJobTypesService } from "@shared/jobs-and-billing-job-types-service/jobs-and-billing-job-types.service";
import { CommonModule } from "@angular/common";
import { JumpstartComponentsModule } from "@wk/components-v3-angular17";
import { SharedModule } from "@shared/shared.module";
import { JobPriority, JobsAndBillingPriorityService } from "@shared/jobs-and-billing-priority";
import { JobsAndBillingCommonService } from "@app/shared/jobs-and-billing-common-service/jobs-and-billing-common.service";

type StatusOption = ITaxReturnStatus & { disabled?: boolean };

@Component({
    selector: "wk-jobs-billing-follow-up-info",
    templateUrl: "./jobs-billing-follow-up-info.component.html",
    styleUrl: "../follow-up-info.component.scss",
    imports: [CommonModule, TranslateModule, JumpstartComponentsModule, SharedModule, WkTranslatePipe],
    host: { class: "wk-fup" },
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [WkTranslatePipe],
})
export class JobsBillingFollowUpInfoComponent {
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly jobsAndBillingJobTypesService = inject(JobsAndBillingJobTypesService);
    private readonly jobsAndBillingPriorityService = inject(JobsAndBillingPriorityService);
    private readonly jobsAndBillingCommonService = inject(JobsAndBillingCommonService);
    private readonly taxReturnStatusesService = inject(TaxReturnStatusesService);
    private readonly translate = inject(TranslateService);

    protected cardData = input<JobsBillingCard>();

    readonly statusChanged = output<{ statusId: string }>();
    readonly priorityChanged = output<{ priority: JobPriority }>();

    private readonly allStatuses = computed(() =>
        this.mappingOfStatusesService
            .getAllReturnStatuses()
            .filter((mergedStatus) => !!mergedStatus?.TaxReturnStatus)
            .map((mergedStatus) => mergedStatus.TaxReturnStatus),
    );

    private readonly cardStatusResource = resource<ITaxReturnStatus | undefined, void>({
        loader: () => {
            const statusId = this.cardData()?.job?.statusId;
            if (!statusId) return Promise.resolve(undefined);
            return this.taxReturnStatusesService.getTaxReturnStatusById(statusId);
        },
    });

    public canEditPriority = computed(() => {
        const cardData = this.cardData();
        return cardData ? this.jobsAndBillingCommonService.hasAccess("job_priority", cardData) : false;
    });

    public canEditStatus = computed(() => {
        const cardData = this.cardData();
        return cardData ? this.jobsAndBillingCommonService.hasAccess("job_status", cardData) : false;
    });

    // The role-status-association endpoint can occasionally be slow (DASH-22444); show a localized loader on the
    // Job Status field only, rather than blocking the rest of the modal, which is already usable in the meantime.
    protected readonly isStatusOptionsLoading = computed(() => this.jobsAndBillingJobTypesService.isRoleStatusAssociationLoading());

    protected readonly statusOptions = computed<StatusOption[]>(() => {
        const jobStatusId = this.cardData()?.job?.statusId;
        const jobTypeId = this.cardData()?.job?.typeId; // Beware the guard below, value can be 0 (falsey) but still valid

        const statuses = this.allStatuses();
        const associationsByJobType = this.jobsAndBillingJobTypesService.roleStatusAssociationByJobType();
        if (!jobStatusId || jobTypeId == null || !statuses.length || !associationsByJobType.size) {
            return [];
        }

        const statusIdsAssociatedWithJobType = associationsByJobType.get(jobTypeId) ?? new Set<string>();
        const activeOptions = statuses.filter((status) => this.filterStatuses(status, statusIdsAssociatedWithJobType));

        const isCurrentStatusInOptions = activeOptions.some((status) => status.Id === jobStatusId);
        if (!isCurrentStatusInOptions) {
            const cardStatus = this.cardStatusResource.value();
            if (cardStatus) {
                const disabledCardStatus: StatusOption = {
                    ...cardStatus,
                    Name: {
                        "fr-CA": this.translate.instant("General.Inactive") + cardStatus.Name["fr-CA"],
                        "en-CA": this.translate.instant("General.Inactive") + cardStatus.Name["en-CA"],
                    },
                    disabled: true,
                };
                return [...activeOptions, disabledCardStatus];
            }
        }

        return activeOptions;
    });

    protected readonly stepName = computed(() => {
        const statusId = this.cardData()?.job?.statusId;
        if (!statusId) {
            return null;
        }
        return this.mappingOfStatusesService.getStepFromStatusId(statusId)?.Name ?? null;
    });

    protected readonly priorityOptions = this.jobsAndBillingPriorityService.jobPriorityOptions;

    private isStatusMapped(statusId: string, statusIdsAssociatedWithJobType: Set<string>): boolean {
        return !!this.mappingOfStatusesService.getStepFromStatusId(statusId) && statusIdsAssociatedWithJobType.has(statusId);
    }

    private filterStatuses(status: ITaxReturnStatus, statusIdsAssociatedWithJobType: Set<string>): boolean {
        return !!status?.Id && status.Active && this.isStatusMapped(status.Id, statusIdsAssociatedWithJobType);
    }

    changeStatus(statusId: string) {
        this.statusChanged.emit({ statusId });
    }

    changePriority(priority: JobPriority) {
        this.priorityChanged.emit({ priority });
    }
}
